import { inject, Injectable } from '@angular/core';
import { DashboardProfileContextService, ProfileIdentifier } from '@rma/dashboard/authentication/public/user';
import { PagedList } from '@rma/dashboard/generic/domain/to-be-sorted';
import { ProfileType, SALES } from '@rma/generic/domain/types';
import { catchAndLogError } from '@rma/generic/rxjs/operators/catch-and-log-error';
import { rmaShareReplay } from '@rma/generic/rxjs/operators/rma-share-replay';
import { cache } from 'decorator-cache-getter';
import { catchError, combineLatest, map, Observable, of, startWith, Subject, switchMap } from 'rxjs';
import { ReviewConversionApiService } from '../api/review-conversion-api.service';
import {
  ConversionEnabled,
  ConvertReviewEnabledResponse,
  CreateOrUpdateConvertReviewDestinationsRequest,
  ProfileConvertDestination,
  ProfileConvertReviewSetting,
} from '../models/google-conversion.model';

@Injectable({
  providedIn: 'root',
})
export class GoogleConversionService {
  private readonly profileContextService = inject(DashboardProfileContextService);
  private readonly reviewConversionApiService = inject(ReviewConversionApiService);
  private refreshSalesGoogleConvertReviewSettings$ = new Subject<boolean>();

  @cache
  public get hasOwnValidSalesGoogleDestination$(): Observable<boolean> {
    return this.ownSalesGoogleConvertReviewDestination$.pipe(
      map((destination) => !!destination),
      catchError((_) => of(false)),
    );
  }

  @cache
  public get salesGoogleConvertReviewDestinations$(): Observable<ProfileConvertDestination[]> {
    return this.salesGoogleConvertReviewSettings$.pipe(
      map((settings) => settings?.map((setting) => this.mapToProfileConvertDestination(setting)) ?? []),
      rmaShareReplay(),
    );
  }

  @cache
  public get salesGoogleConvertReviewDestination$(): Observable<ProfileConvertDestination | undefined> {
    return combineLatest([this.salesGoogleConvertReviewDestinations$, this.profileContextService.profileIdentifier$]).pipe(
      map(([destinations, profileId]) =>
        destinations?.find(
          (destination) => destination.profileType === profileId.profileType && destination.profileCode === profileId.profileCode,
        ),
      ),
      rmaShareReplay(),
    );
  }

  @cache
  public get googleConvertReviewEnableds$(): Observable<ConvertReviewEnabledResponse> {
    return combineLatest([
      this.profileContextService.profileIdentifier$,
      this.refreshSalesGoogleConvertReviewSettings$.pipe(startWith(true)),
    ]).pipe(
      switchMap(([profileId, _]) =>
        this.reviewConversionApiService.getConvertReviewEnableds(profileId.profileType, profileId.profileCode, 'Google'),
      ),
      rmaShareReplay(),
    );
  }

  public updateCurrentContextGoogleConvertReviewDestination(destination: ProfileIdentifier) {
    return this.profileContextService.profileIdentifier$.pipe(
      switchMap((profileId) => this.updateGoogleConvertReviewDestination(profileId.profileType, profileId.profileCode, destination)),
    );
  }

  public updateGoogleConvertReviewDestination(profileType: ProfileType, profileCode: string, destination: ProfileIdentifier) {
    return this.reviewConversionApiService.createOrUpdateConvertReviewDestination(profileType, profileCode, 'Google', {
      directToProfileType: destination.profileType,
      directToProfileCode: destination.profileCode,
    });
  }

  public bulkUpdateGoogleConvertReviewDestinations(request: CreateOrUpdateConvertReviewDestinationsRequest) {
    return this.reviewConversionApiService.createOrUpdateConvertReviewDestinations(request);
  }

  public updateCurrentContextGoogleConvertEnableds(enableds: ConversionEnabled[]) {
    return this.profileContextService.profileIdentifier$.pipe(
      switchMap((profileId) =>
        this.reviewConversionApiService.createOrUpdateConvertReviewEnabled(profileId.profileType, profileId.profileCode, {
          convertReviewEnabled: enableds.map((enabled) => ({
            enabled: enabled.enabled,
            externalReviewSource: 'Google',
            verticalType: enabled.verticalType,
          })),
        }),
      ),
    );
  }

  public applySettingToAllSalesConverted(): Observable<null> {
    return this.profileContextService.profileIdentifier$.pipe(
      switchMap((profileId) =>
        this.reviewConversionApiService.applyDestinationToAll(SALES, profileId.profileType, profileId.profileCode, 'Google'),
      ),
    );
  }

  public refreshSalesGoogleConvertReviewSettings() {
    this.refreshSalesGoogleConvertReviewSettings$.next(true);
  }

  public getPagedSalesGoogleConvertReviewDestinations(pageSize: number, skip: number): Observable<PagedList<ProfileConvertDestination>> {
    return this.getPaginatedSalesGoogleConvertReviewSettings(pageSize, skip).pipe(
      map((settings) => {
        return {
          skip: settings.skip,
          take: settings.take,
          total: settings.total,
          results: settings.results?.map((setting) => this.mapToProfileConvertDestination(setting)) ?? [],
        };
      }),
    );
  }

  public getProfileConvertDestinationsForMember(memberCode?: string): Observable<ProfileConvertDestination | undefined> {
    return this.profileContextService.profileIdentifier$.pipe(
      switchMap((profileId) =>
        this.reviewConversionApiService.getMemberConvertReviewSettings(
          SALES,
          profileId.profileType,
          profileId.profileCode,
          1,
          0,
          'Google',
          memberCode ? [memberCode] : [profileId.profileCode],
        ),
      ),
      map((pagedResults) => (pagedResults.results.length > 0 ? pagedResults.results[0] : undefined)),
      map((profileSetting) => (!profileSetting ? profileSetting : this.mapToProfileConvertDestination(profileSetting))),
    );
  }

  @cache
  private get salesGoogleConvertReviewSettings$() {
    return this.getPaginatedSalesGoogleConvertReviewSettings(2000, 0).pipe(
      map((settings) => settings.results),
      catchAndLogError<ProfileConvertReviewSetting[]>(of([])),
      rmaShareReplay(),
    );
  }

  @cache
  private get ownSalesGoogleConvertReviewDestination$() {
    return this.profileContextService.profileIdentifier$.pipe(
      switchMap((profileId) =>
        this.reviewConversionApiService.getOwnDestinationForProfile(SALES, profileId.profileType, profileId.profileCode, 'Google'),
      ),
      rmaShareReplay(),
    );
  }

  private getPaginatedSalesGoogleConvertReviewSettings(pageSize: number, skip: number): Observable<PagedList<ProfileConvertReviewSetting>> {
    return combineLatest([
      this.profileContextService.profileIdentifier$,
      this.refreshSalesGoogleConvertReviewSettings$.pipe(startWith(true)),
    ]).pipe(
      switchMap(([profileId, _]) =>
        this.reviewConversionApiService.getMemberConvertReviewSettings(
          SALES,
          profileId.profileType,
          profileId.profileCode,
          pageSize,
          skip,
          'Google',
          undefined,
        ),
      ),
    );
  }

  private mapToProfileConvertDestination(setting: ProfileConvertReviewSetting): ProfileConvertDestination {
    return {
      profileAvatar: setting.profileAvatar,
      profileName: setting.profileName,
      profileCode: setting.profileCode,
      profileType: setting.profileType,
      hasEditAccess: setting.hasEditAccess,
      editAccessProfileName: setting.editAccessProfileName,
      currentDestination: setting.currentConvertReviewDestination
        ? {
            profileCode: setting.currentConvertReviewDestination.ownersProfileCode,
            profileType: setting.currentConvertReviewDestination.ownersProfileType,
            externalProfileId: setting.currentConvertReviewDestination.externalProfileId,
          }
        : undefined,
      potentialDestinations:
        setting.potentialConvertReviewDestinations?.map((destination) => ({
          profileCode: destination.convertReviewDestination.ownersProfileCode,
          profileType: destination.convertReviewDestination.ownersProfileType,
          externalProfileId: destination.convertReviewDestination.externalProfileId,
          profileName: destination.profileName,
          isOwnDestination: compareProfileIdentifiers(
            {
              profileCode: destination.convertReviewDestination.ownersProfileCode,
              profileType: destination.convertReviewDestination.ownersProfileType,
            },
            {
              profileType: setting.profileType,
              profileCode: setting.profileCode,
            },
          ),
        })) ?? [],
    };
  }
}

export function compareProfileIdentifiers(destination1?: ProfileIdentifier, destination2?: ProfileIdentifier): boolean {
  return destination1?.profileCode === destination2?.profileCode && destination1?.profileType === destination2?.profileType;
}
